import { Controller } from "@hotwired/stimulus"
import flatpickr from "flatpickr"
import { Mandarin } from "flatpickr/dist/l10n/zh.js"
import { KTToast } from "@keenthemes/ktui"

const FMT = "Y-m-d"

export default class extends Controller {
  static targets = ["input", "preset"]
  static values = {
    startDate: String,
    endDate: String,
    noActiveStyle: Boolean,
    maxDays: { type: Number, default: 30 },
    maxDate: String,
  }

  connect() {
    const hooks = [this.#validate, this.#sync, this.#emit]
    this.picker = flatpickr(this.inputTarget, {
      mode: "range",
      dateFormat: FMT,
      locale: {
        ...Mandarin,
        firstDayOfWeek: 1,
        rangeSeparator: " ~ ",
        weekdays: {
          shorthand: ["日", "一", "二", "三", "四", "五", "六"],
          longhand: Mandarin.weekdays.longhand,
        },
      },
      minDate: "2025-01-01",
      maxDate: this.maxDateValue
        ? flatpickr.parseDate(this.maxDateValue, FMT)
        : this.#nextTomorrow,
      defaultDate: this.#initRange(),
      onReady: hooks,
      onValueUpdate: hooks,
      onOpen: (d) => {
        this._saved = d
        if (this.noActiveStyleValue) this.inputTarget.classList.remove("active")
      },
      onClose: (d, s, fp) => {
        if (d.length !== 2 || !s.includes("~")) fp.setDate(this._saved)
        this.#sync(fp.selectedDates)
      },
    })
  }

  disconnect() {
    this.picker?.destroy()
    KTToast.clearAll()
  }

  selectPreset({ params: { days } }) {
    if (days) this.picker.setDate(this.#range(days), true)
  }

  setRange(startDate, endDate) {
    const s = this.#parse(startDate),
      e = this.#parse(endDate)
    if (!s || !e) return
    this._k = `${this.#f(s)}-${this.#f(e)}`
    this.picker?.setDate([s, e], false)
  }

  #validate = (d, _, fp) => {
    if (d.length === 2 && this.#days(d) > this.maxDaysValue) {
      this.#warn()
      const end = d[1],
        start = new Date(end)
      start.setDate(end.getDate() - this.maxDaysValue)
      const fixed = [start, end]
      fp.setDate(fixed)
      d[0] = fixed[0]
      d[1] = fixed[1]
    }
  }

  #sync = (d) => {
    this.#withDates(d, (s, e) => {
      const key = this.#key([s, e])
      let hit = null
      for (const b of this.presetTargets) {
        const days = +(b.dataset.dateRangeDaysParam || 0)
        const match = key === this.#key(this.#range(days))
        b.classList.toggle("active", match)
        if (match) hit = days
      }
      this.inputTarget.classList.toggle(
        "active",
        !hit && !this.noActiveStyleValue,
      )
    })
  }

  #emit = (d) => {
    this.#withDates(d, (s, e) => {
      const detail = { startDate: this.#f(s), endDate: this.#f(e) }
      const k = `${detail.startDate}-${detail.endDate}`
      if (this._k === k) return
      this._k = k
      this.dispatch("change", { detail, bubbles: true })
    })
  }

  #initRange() {
    const s = this.#parse(this.startDateValue),
      e = this.#parse(this.endDateValue)
    return s && e ? [s, e] : this.#range(7)
  }

  #withDates(d, cb) {
    const [s, e] = d || []
    if (s && e) cb(s, e)
  }

  #warn(message = `最长查询范围为${this.maxDaysValue}天`) {
    if (this._w) return
    KTToast.show({ variant: "destructive", message })
    this._w = true
  }

  #range(n) {
    const end = this.#yesterday,
      start = new Date(end)
    start.setDate(end.getDate() - (n - 1))
    return [start, end]
  }

  #days = ([a, b]) => Math.round((b - a) / 864e5)
  #parse = (v) => (v ? flatpickr.parseDate(v, FMT) : null)
  #f = (d) => flatpickr.formatDate(d, FMT)
  #key = ([s, e]) => `${this.#f(s)}-${this.#f(e)}`

  get #yesterday() {
    const d = new Date()
    d.setDate(d.getDate() - 1)
    d.setHours(23, 59, 59, 999)
    return d
  }

  get #nextTomorrow() {
    const d = new Date()
    d.setDate(d.getDate() + 2)
    d.setHours(23, 59, 59, 999)
    return d
  }
}
